import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { API_ENDPOINTS } from '../config/api';
import EmptyState from '../components/UI/EmptyState';
import { ErrandCardSkeleton } from '../components/UI/LoadingSkeleton';
import { getErrorMessage } from '../utils/errorMessages';
import { showError } from '../utils/toast';

interface CourierEarning {
  id: string;
  amount: number;
  earnings_type: string;
  status: string;
  payment_method: string;
  paid_at: string;
  notes: string;
  created_at: string;
  errand: {
    title: string;
    description: string;
  }[];
  escrow_request: {
    amount: number;
    total_amount: number;
  }[];
}

const CourierEarnings: React.FC = () => {
  const [earnings, setEarnings] = useState<CourierEarning[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalEarnings, setTotalEarnings] = useState(0);
  const [pendingEarnings, setPendingEarnings] = useState(0);

  const fetchEarnings = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get user ID from JWT token
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${API_ENDPOINTS.BASE}/api/auth/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to get user profile');
      }

      const profileData = await response.json();
      const courierId = profileData.profile.id;

      // Fetch earnings from both courier_earnings and driver_earnings tables
      const [courierEarningsResult, driverEarningsResult] = await Promise.all([
        supabase
          .from('courier_earnings')
          .select(`
            id,
            amount,
            earnings_type,
            status,
            payment_method,
            paid_at,
            notes,
            created_at,
            errand:errands(
              title,
              description
            ),
            escrow_request:escrow_requests(
              amount,
              total_amount
            )
          `)
          .eq('courier_id', courierId)
          .order('created_at', { ascending: false }),
        supabase
          .from('driver_earnings')
          .select(`
            id,
            driver_share as amount,
            commission_status as status,
            created_at,
            errand:errands(
              title,
              description
            )
          `)
          .eq('driver_id', courierId)
          .order('created_at', { ascending: false })
      ]);

      if (courierEarningsResult.error && driverEarningsResult.error) {
        throw courierEarningsResult.error;
      }

      // Combine earnings from both sources
      const courierEarnings = (courierEarningsResult.data || []).map((ce: any) => ({
        ...ce,
        errand: Array.isArray(ce.errand) ? ce.errand : (ce.errand ? [ce.errand] : []),
        escrow_request: Array.isArray(ce.escrow_request) ? ce.escrow_request : (ce.escrow_request ? [ce.escrow_request] : [])
      }));

      const driverEarnings = (driverEarningsResult.data || []).map((de: any) => ({
        id: de.id,
        amount: de.amount || 0,
        earnings_type: 'errand',
        status: de.status === 'paid' ? 'completed' : de.status === 'due' ? 'pending' : 'pending',
        payment_method: 'cash_on_delivery',
        paid_at: de.status === 'paid' ? de.created_at : null,
        notes: 'Driver earnings from errand',
        created_at: de.created_at,
        errand: de.errand ? (Array.isArray(de.errand) ? de.errand : [de.errand]) : [],
        escrow_request: []
      }));

      const allEarnings = [...courierEarnings, ...driverEarnings].sort((a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );

      setEarnings(allEarnings);

      // Calculate totals
      const total = allEarnings
        .filter(e => e.status === 'completed' || e.status === 'paid')
        .reduce((sum, earning) => sum + (earning.amount || 0), 0);
      const pending = allEarnings
        .filter(e => e.status === 'pending' || e.status === 'due')
        .reduce((sum, earning) => sum + (earning.amount || 0), 0);

      setTotalEarnings(total);
      setPendingEarnings(pending);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch earnings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEarnings();
  }, []);

  // Listen for errand payment marked events and errand completion to refresh earnings
  useEffect(() => {
    const handlePaymentMarked = () => {
      console.log('Errand payment marked, refreshing courier earnings...');
      fetchEarnings();
    };

    const handleErrandCompleted = () => {
      console.log('Errand completed, refreshing courier earnings...');
      fetchEarnings();
    };

    window.addEventListener('errandPaymentMarked', handlePaymentMarked);
    window.addEventListener('errandCompleted', handleErrandCompleted);

    // Also refresh when page becomes visible (user switches back to tab)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchEarnings();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('errandPaymentMarked', handlePaymentMarked);
      window.removeEventListener('errandCompleted', handleErrandCompleted);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'paid': return '✅';
      case 'pending': return '⏳';
      case 'cancelled': return '❌';
      default: return '📋';
    }
  };

  const getEarningsTypeIcon = (type: string) => {
    switch (type) {
      case 'escrow': return '🔒';
      case 'regular': return '🚚';
      case 'bonus': return '🎁';
      default: return '💰';
    }
  };

  // Show error toast if error exists
  useEffect(() => {
    if (error) {
      showError(getErrorMessage(error, 'Failed to load earnings'));
    }
  }, [error]);

  if (loading && earnings.length === 0) {
    return (
      <div className="space-y-4">
        <ErrandCardSkeleton />
        <ErrandCardSkeleton />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-neutral-800 mb-2">My Earnings</h1>
        <p className="text-neutral-600">
          Track your earnings from completed errands and escrow payments.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Enhanced Earnings Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-medium p-6 border border-neutral-200">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-sm font-medium text-neutral-600">Total Earnings</p>
              <p className="text-2xl font-bold text-green-600">P{totalEarnings.toFixed(2)}</p>
              <div className="flex items-center mt-2">
                <span className="text-xs text-green-500 bg-green-50 px-2 py-1 rounded-full">
                  All time
                </span>
              </div>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <span className="text-2xl">💰</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-medium p-6 border border-neutral-200">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-sm font-medium text-neutral-600">Paid Earnings</p>
              <p className="text-2xl font-bold text-blue-600">P{(totalEarnings - pendingEarnings).toFixed(2)}</p>
              <div className="flex items-center mt-2">
                <span className="text-xs text-blue-500 bg-blue-50 px-2 py-1 rounded-full">
                  Available now
                </span>
              </div>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <span className="text-2xl">✅</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-medium p-6 border border-neutral-200">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-sm font-medium text-neutral-600">Pending Earnings</p>
              <p className="text-2xl font-bold text-orange-600">P{pendingEarnings.toFixed(2)}</p>
              <div className="flex items-center mt-2">
                <span className="text-xs text-orange-500 bg-orange-50 px-2 py-1 rounded-full">
                  In processing
                </span>
              </div>
            </div>
            <div className="p-3 bg-orange-100 rounded-lg">
              <span className="text-2xl">⏳</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-medium p-6 border border-neutral-200">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-sm font-medium text-neutral-600">Avg. per Job</p>
              <p className="text-2xl font-bold text-purple-600">
                P{earnings.length > 0 ? (totalEarnings / earnings.length).toFixed(2) : '0.00'}
              </p>
              <div className="flex items-center mt-2">
                <span className="text-xs text-purple-500 bg-purple-50 px-2 py-1 rounded-full">
                  {earnings.length} jobs
                </span>
              </div>
            </div>
            <div className="p-3 bg-purple-100 rounded-lg">
              <span className="text-2xl">🎯</span>
            </div>
          </div>
        </div>
      </div>

      {/* Earnings Insights */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-medium p-6 border border-neutral-200">
          <h3 className="text-lg font-semibold text-neutral-800 mb-4 flex items-center">
            <span className="text-xl mr-2">📊</span>
            Earnings Breakdown
          </h3>
          <div className="space-y-3">
            {(() => {
              const deliveryEarnings = earnings.filter(e => e.earnings_type === 'regular' || e.earnings_type === '');
              const errandEarnings = earnings.filter(e => e.earnings_type === 'errand');
              const escrowEarnings = earnings.filter(e => e.earnings_type === 'escrow');

              const deliveryTotal = deliveryEarnings.reduce((sum, e) => sum + (e.amount || 0), 0);
              const errandTotal = errandEarnings.reduce((sum, e) => sum + (e.amount || 0), 0);
              const escrowTotal = escrowEarnings.reduce((sum, e) => sum + (e.amount || 0), 0);

              return (
                <>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-neutral-600">Delivery Earnings</span>
                    <span className="text-sm font-medium text-neutral-800">P{deliveryTotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-neutral-600">Errand Earnings</span>
                    <span className="text-sm font-medium text-neutral-800">P{errandTotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-neutral-600">Escrow Earnings</span>
                    <span className="text-sm font-medium text-neutral-800">P{escrowTotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center border-t border-neutral-100 pt-2">
                    <span className="text-sm font-medium text-neutral-800">Total</span>
                    <span className="text-sm font-bold text-green-600">P{totalEarnings.toFixed(2)}</span>
                  </div>
                </>
              );
            })()}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-medium p-6 border border-neutral-200">
          <h3 className="text-lg font-semibold text-neutral-800 mb-4 flex items-center">
            <span className="text-xl mr-2">📈</span>
            Payment Status
          </h3>
          <div className="space-y-3">
            {(() => {
              const paidCount = earnings.filter(e => e.status === 'paid' || e.status === 'completed').length;
              const pendingCount = earnings.filter(e => e.status === 'pending' || e.status === 'due').length;
              const cancelledCount = earnings.filter(e => e.status === 'cancelled').length;

              return (
                <>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-neutral-600">Paid</span>
                    <div className="flex items-center">
                      <span className="text-sm font-medium text-green-600 mr-2">{paidCount}</span>
                      <div className="w-16 bg-neutral-200 rounded-full h-2">
                        <div
                          className="bg-green-500 h-2 rounded-full"
                          style={{ width: `${earnings.length > 0 ? (paidCount / earnings.length) * 100 : 0}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-neutral-600">Pending</span>
                    <div className="flex items-center">
                      <span className="text-sm font-medium text-orange-600 mr-2">{pendingCount}</span>
                      <div className="w-16 bg-neutral-200 rounded-full h-2">
                        <div
                          className="bg-orange-500 h-2 rounded-full"
                          style={{ width: `${earnings.length > 0 ? (pendingCount / earnings.length) * 100 : 0}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-neutral-600">Cancelled</span>
                    <div className="flex items-center">
                      <span className="text-sm font-medium text-red-600 mr-2">{cancelledCount}</span>
                      <div className="w-16 bg-neutral-200 rounded-full h-2">
                        <div
                          className="bg-red-500 h-2 rounded-full"
                          style={{ width: `${earnings.length > 0 ? (cancelledCount / earnings.length) * 100 : 0}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      </div>

      {earnings.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-16 h-16 mx-auto mb-3 text-gray-300">
            <svg fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-neutral-800 mb-2">No Earnings Yet</h3>
          <p className="text-neutral-600">
            Complete errands to start earning money.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {earnings.map((earning) => (
            <div key={earning.id} className="bg-white rounded-lg shadow-medium p-6 border border-neutral-200">
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center">
                  <span className="text-2xl mr-3">{getEarningsTypeIcon(earning.earnings_type)}</span>
                  <div>
                    <h3 className="text-lg font-semibold text-neutral-800">
                      {earning.errand?.[0]?.title || 'Untitled Errand'}
                    </h3>
                    <p className="text-sm text-neutral-600">
                      {earning.earnings_type.charAt(0).toUpperCase() + earning.earnings_type.slice(1)} Earnings
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(earning.status)}`}>
                    {getStatusIcon(earning.status)} {earning.status.toUpperCase()}
                  </div>
                  <div className="text-lg font-bold text-primary-700 mt-1">
                    P{earning.amount.toFixed(2)}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <h4 className="font-medium text-neutral-700 mb-2">Payment Details</h4>
                  <div className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span className="text-neutral-600">Payment Method:</span>
                      <span className="text-neutral-800">{earning.payment_method.replace('_', ' ').toUpperCase()}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-neutral-600">Earnings Type:</span>
                      <span className="text-neutral-800">{earning.earnings_type.charAt(0).toUpperCase() + earning.earnings_type.slice(1)}</span>
                    </div>
                    {earning.paid_at && (
                      <div className="flex justify-between text-sm">
                        <span className="text-neutral-600">Paid Date:</span>
                        <span className="text-neutral-800">
                          {new Date(earning.paid_at).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <h4 className="font-medium text-neutral-700 mb-2">Errand Details</h4>
                  <div className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span className="text-neutral-600">Created:</span>
                      <span className="text-neutral-800">
                        {new Date(earning.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    {earning.errand?.[0]?.description && (
                      <div className="text-sm text-neutral-600">
                        {earning.errand[0].description}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {earning.notes && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <h4 className="font-medium text-blue-800 mb-1">Notes</h4>
                  <p className="text-blue-700 text-sm">{earning.notes}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CourierEarnings;
